import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Boxes, Pencil, Archive, ExternalLink, ShieldCheck, ShieldAlert, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { workspacesApi, type CreateWorkspaceInput } from '@/api/workspaces';
import { extractApiError } from '@/api/client';
import Modal from '@/components/ui/Modal';
import TotpModal from '@/components/auth/TotpModal';
import UserPicker, { type PickedUser } from '@/components/ui/UserPicker';

/**
 * Phase 12.1 — admin overview & creator for workspaces.
 *
 * SUPER_ADMIN-only (gated by AdminRoute + backend role check). Sensitive
 * mutations are wrapped in `runWithTotp()` which retries with X-TOTP-Code
 * after the user enters a fresh code in TotpModal.
 */
export default function AdminWorkspaces() {
  const qc = useQueryClient();
  const { data: list, isLoading } = useQuery({
    queryKey: ['admin-workspaces'],
    queryFn: workspacesApi.list,
  });

  const [createOpen, setCreateOpen] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState<{ slug: string; reason: string } | null>(null);
  // Phase 12.1 — per-row provisioning state. Ключ = slug.
  const [provisionBusy, setProvisionBusy] = useState<Record<string, boolean>>({});
  const [provisionResult, setProvisionResult] = useState<{ slug: string; ok: boolean; log: string } | null>(null);

  // TOTP retry state shared across all sensitive ops on this page.
  const [totpOpen, setTotpOpen] = useState(false);
  const [totpError, setTotpError] = useState<{
    code: 'TOTP_REQUIRED' | 'TOTP_CODE_REQUIRED' | 'TOTP_INVALID' | null;
    message: string | null;
  }>({ code: null, message: null });
  const [totpLoading, setTotpLoading] = useState(false);
  // The pending operation to retry once we have a code.
  const pendingOpRef = useState<{ run: (code?: string) => Promise<void> } | null>(null);

  /**
   * Helper: invoke a sensitive op. First call w/o code; if backend says
   * TOTP_CODE_REQUIRED, open modal and retry with code. TOTP_REQUIRED →
   * open modal in enrollment-link mode. Any other error → throw to caller.
   */
  async function runWithTotp(op: (code?: string) => Promise<unknown>): Promise<void> {
    pendingOpRef[1]({ run: async (code) => { await op(code); } });
    try {
      await op(); // first attempt — no code
      pendingOpRef[1](null);
    } catch (e) {
      const apiErr = extractApiError(e);
      if (apiErr.code === 'TOTP_REQUIRED') {
        setTotpError({ code: 'TOTP_REQUIRED', message: apiErr.message });
        setTotpOpen(true);
        return;
      }
      if (apiErr.code === 'TOTP_CODE_REQUIRED') {
        setTotpError({ code: 'TOTP_CODE_REQUIRED', message: null });
        setTotpOpen(true);
        return;
      }
      pendingOpRef[1](null);
      throw e;
    }
  }

  async function handleTotpSubmit(code: string) {
    const pending = pendingOpRef[0];
    if (!pending) {
      setTotpOpen(false);
      return;
    }
    setTotpLoading(true);
    try {
      await pending.run(code);
      setTotpOpen(false);
      setTotpError({ code: null, message: null });
      pendingOpRef[1](null);
    } catch (e) {
      const apiErr = extractApiError(e);
      if (apiErr.code === 'TOTP_INVALID') {
        setTotpError({ code: 'TOTP_INVALID', message: apiErr.message });
      } else {
        setTotpError({ code: null, message: apiErr.message });
      }
    } finally {
      setTotpLoading(false);
    }
  }

  const refreshList = () => qc.invalidateQueries({ queryKey: ['admin-workspaces'] });

  // Phase 12.1 — кнопка "Provision". Дёргает host-скрипт через backend→daemon.
  async function provisionWorkspaceSlug(slug: string) {
    setProvisionBusy((s) => ({ ...s, [slug]: true }));
    try {
      await runWithTotp(async (code) => {
        const result = await workspacesApi.provision(slug, code);
        setProvisionResult({ slug, ok: result.ok, log: result.log });
        refreshList();
      });
    } catch (e) {
      const msg = extractApiError(e).message;
      setProvisionResult({ slug, ok: false, log: `Ошибка: ${msg}` });
    } finally {
      setProvisionBusy((s) => ({ ...s, [slug]: false }));
    }
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-surface-50 flex items-center gap-2">
          <Boxes size={22} className="text-brand-400" /> Workspaces
        </h1>
        <button onClick={() => setCreateOpen(true)} className="btn-primary inline-flex items-center gap-2">
          <Plus size={16} /> Создать
        </button>
      </div>

      {isLoading ? (
        <div className="text-surface-500">Загрузка…</div>
      ) : !list || list.length === 0 ? (
        <div className="bg-surface-900 border border-surface-800 rounded-xl p-12 text-center text-surface-400">
          <Boxes size={36} className="mx-auto mb-3 text-surface-600" />
          <p className="text-sm">Нет ни одного workspace. Нажмите «Создать», чтобы начать.</p>
        </div>
      ) : (
        <div className="bg-surface-900 border border-surface-800 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-surface-800">
                <th className="text-left px-4 py-3 text-surface-400 font-medium">Workspace</th>
                <th className="text-left px-4 py-3 text-surface-400 font-medium">Slug / Subdomain</th>
                <th className="text-left px-4 py-3 text-surface-400 font-medium">Status</th>
                <th className="text-left px-4 py-3 text-surface-400 font-medium">Created</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {list.map((ws) => (
                <tr key={ws.id} className="border-b border-surface-800/50 hover:bg-surface-800/30">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {ws.brandColor && (
                        <span
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ background: ws.brandColor }}
                        />
                      )}
                      <p className="font-medium text-surface-100">{ws.displayName}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <code className="text-xs text-surface-300 font-mono">{ws.slug}</code>
                      <a
                        href={`https://${ws.slug}.${import.meta.env.VITE_MAIN_DOMAIN ?? 'chat56.ru'}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-surface-500 hover:text-brand-400"
                        title="Открыть subdomain"
                      >
                        <ExternalLink size={12} />
                      </a>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {ws.archived ? (
                      <span className="px-2 py-0.5 rounded text-xs bg-amber-900/40 text-amber-300">archived</span>
                    ) : (
                      <span className="px-2 py-0.5 rounded text-xs bg-emerald-900/40 text-emerald-300">active</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-surface-500 text-xs">
                    {format(new Date(ws.createdAt), 'MMM d, yyyy')}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 justify-end">
                      {!ws.archived && (
                        <button
                          onClick={() => provisionWorkspaceSlug(ws.slug)}
                          disabled={!!provisionBusy[ws.slug]}
                          className="btn-ghost text-xs px-2 py-1"
                          title={
                            ws.certIssuedAt
                              ? `Последний провижн: ${format(new Date(ws.certIssuedAt), 'dd.MM.yyyy HH:mm')}. Нажмите чтобы перевыпустить cert и обновить nginx.`
                              : 'Выпустить SSL и сконфигурировать nginx для subdomain'
                          }
                        >
                          {provisionBusy[ws.slug] ? (
                            <Loader2 size={13} className="animate-spin text-brand-400" />
                          ) : ws.certIssuedAt ? (
                            <ShieldCheck size={13} className="text-emerald-400" />
                          ) : (
                            <ShieldAlert size={13} className="text-amber-400" />
                          )}
                        </button>
                      )}
                      <Link to={`/admin/workspaces/${ws.slug}`} className="btn-ghost text-xs px-2 py-1">
                        <Pencil size={13} />
                      </Link>
                      {!ws.archived && (
                        <button
                          onClick={() => setArchiveTarget({ slug: ws.slug, reason: '' })}
                          className="btn-ghost text-xs px-2 py-1 text-amber-400"
                          title="Закрыть workspace"
                        >
                          <Archive size={13} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create modal */}
      <CreateWorkspaceForm
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={async (payload, code) => workspacesApi.create(payload, code)}
        runWithTotp={runWithTotp}
        onSuccess={() => { setCreateOpen(false); refreshList(); }}
      />

      {/* Archive dialog */}
      <ArchiveWorkspaceDialog
        target={archiveTarget}
        onClose={() => setArchiveTarget(null)}
        runWithTotp={runWithTotp}
        onSuccess={() => { setArchiveTarget(null); refreshList(); }}
      />

      <TotpModal
        open={totpOpen}
        onClose={() => { setTotpOpen(false); pendingOpRef[1](null); setTotpError({ code: null, message: null }); }}
        onSubmit={handleTotpSubmit}
        errorCode={totpError.code}
        errorMessage={totpError.message}
        loading={totpLoading}
        actionLabel="Подтверждение действия с workspace"
      />

      {/* Phase 12.1 — лог провижна. Показывает stdout host-скрипта (certbot +
          nginx), чтобы админ видел что пошло не так (DNS mismatch, LE rate
          limit, nginx syntax error, etc.) */}
      <Modal
        open={provisionResult !== null}
        onClose={() => setProvisionResult(null)}
        title={provisionResult?.ok ? `✅ Provision успешен: ${provisionResult?.slug}` : `❌ Provision провалился: ${provisionResult?.slug}`}
        size="lg"
      >
        <pre className="text-[11px] leading-snug bg-surface-950 border border-surface-800 rounded p-3 max-h-96 overflow-auto whitespace-pre-wrap text-surface-300">
          {provisionResult?.log || '(no log)'}
        </pre>
        <div className="mt-3 flex justify-end">
          <button onClick={() => setProvisionResult(null)} className="btn-primary text-sm">Закрыть</button>
        </div>
      </Modal>
    </div>
  );
}

// ─── Create form ────────────────────────────────────────────────────────────

function CreateWorkspaceForm({
  open, onClose, onSubmit, runWithTotp, onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: CreateWorkspaceInput, totpCode?: string) => Promise<unknown>;
  runWithTotp: (op: (code?: string) => Promise<unknown>) => Promise<void>;
  onSuccess: () => void;
}) {
  // Phase 13 — owner выбирается автокомплитом (UserPicker), не вводом ID.
  const [owner, setOwner] = useState<PickedUser | null>(null);
  const [slug, setSlug] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [brandColor, setBrandColor] = useState('#7c3aed');
  const [iconUrl, setIconUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setOwner(null); setSlug(''); setDisplayName('');
    setBrandColor('#7c3aed'); setIconUrl(''); setError(null);
  };

  const handleClose = () => { reset(); onClose(); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!owner) { setError('Выберите будущего владельца workspace'); return; }
    setSubmitting(true);
    const payload: CreateWorkspaceInput = {
      ownerUserId: owner.id,
      slug: slug.trim().toLowerCase(),
      displayName: displayName.trim(),
      brandColor: brandColor || null,
      iconUrl: iconUrl.trim() || null,
    };
    try {
      await runWithTotp(async (code) => {
        await onSubmit(payload, code);
        reset();
        onSuccess();
      });
    } catch (e2) {
      const apiErr = extractApiError(e2);
      setError(apiErr.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose} title="Создать workspace" size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="bg-red-900/40 border border-red-700 text-red-300 text-sm rounded-lg px-3 py-2">
            {error}
          </div>
        )}
        <div>
          <label className="block text-surface-300 text-xs font-medium mb-1.5">Будущий владелец</label>
          <UserPicker value={owner} onChange={setOwner} placeholder="Имя или @username…" />
        </div>
        <div>
          <label className="block text-surface-300 text-xs font-medium mb-1.5">Slug (subdomain)</label>
          <input
            value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
            className="irc-input font-mono" placeholder="acme"
            minLength={3} maxLength={32} required
          />
          <p className="text-xs text-surface-500 mt-1">
            3..32 символа, [a-z0-9-]. Будет доступен как <code>{slug || '…'}.{import.meta.env.VITE_MAIN_DOMAIN ?? 'chat56.ru'}</code>.
          </p>
        </div>
        <div>
          <label className="block text-surface-300 text-xs font-medium mb-1.5">Отображаемое название</label>
          <input
            value={displayName} onChange={(e) => setDisplayName(e.target.value)}
            className="irc-input" placeholder="ACME Corp" maxLength={64} required
          />
        </div>
        <div className="flex gap-3">
          <div className="flex-shrink-0">
            <label className="block text-surface-300 text-xs font-medium mb-1.5">Цвет</label>
            <input
              type="color" value={brandColor}
              onChange={(e) => setBrandColor(e.target.value)}
              className="h-9 w-12 border border-surface-700 bg-surface-800 rounded cursor-pointer"
            />
          </div>
          <div className="flex-1">
            <label className="block text-surface-300 text-xs font-medium mb-1.5">Icon URL (опционально)</label>
            <input
              value={iconUrl} onChange={(e) => setIconUrl(e.target.value)}
              className="irc-input" placeholder="https://…/icon.png" type="url"
            />
          </div>
        </div>
        <div className="flex gap-3 justify-end pt-2">
          <button type="button" onClick={handleClose} className="btn-ghost" disabled={submitting}>Отмена</button>
          <button type="submit" className="btn-primary" disabled={submitting}>
            {submitting ? 'Создаём…' : 'Создать'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ─── Archive dialog ─────────────────────────────────────────────────────────

function ArchiveWorkspaceDialog({
  target, onClose, runWithTotp, onSuccess,
}: {
  target: { slug: string; reason: string } | null;
  onClose: () => void;
  runWithTotp: (op: (code?: string) => Promise<unknown>) => Promise<void>;
  onSuccess: () => void;
}) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!target) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (reason.trim().length === 0) { setError('Опишите причину'); return; }
    setSubmitting(true);
    try {
      await runWithTotp(async (code) => {
        await workspacesApi.archive(target.slug, reason.trim(), code);
        setReason('');
        onSuccess();
      });
    } catch (e2) {
      setError(extractApiError(e2).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open onClose={onClose} title={`Закрыть workspace «${target.slug}»`}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="bg-red-900/40 border border-red-700 text-red-300 text-sm rounded-lg px-3 py-2">
            {error}
          </div>
        )}
        <p className="text-sm text-surface-300">
          Workspace перейдёт в архив. На subdomain начнёт показываться сообщение
          с этой причиной + редирект на основной сайт. Данные не удаляются.
        </p>
        <div>
          <label className="block text-surface-300 text-xs font-medium mb-1.5">Причина (увидят пользователи)</label>
          <textarea
            value={reason} onChange={(e) => setReason(e.target.value)}
            className="irc-input min-h-[80px]" placeholder="Например: проект завершён"
            maxLength={500} required
          />
        </div>
        <div className="flex gap-3 justify-end">
          <button type="button" onClick={onClose} className="btn-ghost" disabled={submitting}>Отмена</button>
          <button type="submit" className="btn-primary bg-amber-600 hover:bg-amber-700" disabled={submitting}>
            {submitting ? 'Архивируем…' : 'Закрыть workspace'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
